import { colorStopToString, fixTransparentColorStops } from '../color-utils.js';

/**
 * Parse position value to pixel coordinate
 * @param {Object} posValue - Position value object { type, value }
 * @param {number} size - Size of the dimension (width or height)
 * @param {number} offset - Offset (x or y)
 * @returns {number} Pixel coordinate
 */
function parsePositionValue(posValue, size, offset) {
  if (!posValue) return offset + size / 2; // Default to center

  if (posValue.type === '%') {
    return offset + (parseFloat(posValue.value) / 100) * size;
  } else if (posValue.type === 'px') {
    return offset + parseFloat(posValue.value);
  } else if (posValue.type === 'position-keyword') {
    const keyword = posValue.value.toLowerCase();
    switch (keyword) {
      case 'left':
      case 'top':
        return offset;
      case 'right':
      case 'bottom':
        return offset + size;
      case 'center':
      default:
        return offset + size / 2;
    }
  }

  return offset + size / 2; // Default to center
}

/**
 * Calculate radius based on extent keyword for radial gradients with custom position
 * @param {string} extent - Extent keyword (closest-side, farthest-side, closest-corner, farthest-corner)
 * @param {boolean} isCircle - Whether the gradient is a circle
 * @param {number} distToLeft - Distance from center to left edge
 * @param {number} distToRight - Distance from center to right edge
 * @param {number} distToTop - Distance from center to top edge
 * @param {number} distToBottom - Distance from center to bottom edge
 * @returns {number} Calculated radius
 */
function calculateExtentRadiusFromPosition(
  extent,
  isCircle,
  distToLeft,
  distToRight,
  distToTop,
  distToBottom
) {
  // Calculate distances to corners
  const distToTopLeft = Math.sqrt(distToLeft * distToLeft + distToTop * distToTop);
  const distToTopRight = Math.sqrt(distToRight * distToRight + distToTop * distToTop);
  const distToBottomLeft = Math.sqrt(distToLeft * distToLeft + distToBottom * distToBottom);
  const distToBottomRight = Math.sqrt(distToRight * distToRight + distToBottom * distToBottom);

  switch (extent) {
    case 'closest-side':
      return Math.min(distToLeft, distToRight, distToTop, distToBottom);
    case 'farthest-side':
      return Math.max(distToLeft, distToRight, distToTop, distToBottom);
    case 'closest-corner':
      return Math.min(distToTopLeft, distToTopRight, distToBottomLeft, distToBottomRight);
    case 'farthest-corner':
    default:
      // Default for CSS radial-gradient is farthest-corner
      return Math.max(distToTopLeft, distToTopRight, distToBottomLeft, distToBottomRight);
  }
}

/**
 * Render a repeating radial gradient
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient} Canvas gradient object
 */
export function renderRepeatingRadialGradient(context, gradientDef, { x, y, w, h }) {
  // Parse radial gradient shape and size from orientation
  const orientation = gradientDef.orientation
    ? gradientDef.orientation[0]
    : { type: 'shape', value: 'ellipse' };

  const isCircle = orientation?.type === 'shape' && orientation.value === 'circle';

  // Get extent keyword if specified, default to 'farthest-corner' per CSS spec
  let extentKeyword = 'farthest-corner';
  let explicitRadius = null;

  if (orientation?.style) {
    if (orientation.style.type === 'extent-keyword') {
      extentKeyword = orientation.style.value;
    } else if (orientation.style.type === 'px') {
      explicitRadius = parseFloat(orientation.style.value);
    } else if (orientation.style.type === '%') {
      explicitRadius = (parseFloat(orientation.style.value) / 100) * Math.max(w, h);
    }
  }

  // Parse position from "at" clause
  const atPosition = orientation?.at?.value;
  const centerX = parsePositionValue(atPosition?.x, w, x);
  const centerY = parsePositionValue(atPosition?.y, h, y);

  // Calculate distances from center to edges for extent calculation
  const distToLeft = centerX - x;
  const distToRight = x + w - centerX;
  const distToTop = centerY - y;
  const distToBottom = y + h - centerY;

  // Calculate max radius
  let maxRadius;
  if (explicitRadius !== null) {
    maxRadius = explicitRadius;
  } else {
    maxRadius = calculateExtentRadiusFromPosition(
      extentKeyword,
      isCircle,
      distToLeft,
      distToRight,
      distToTop,
      distToBottom
    );
  }

  // Create radial gradient
  const gradient = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, maxRadius);

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  // Calculate pattern length from color stops
  // For repeating gradients, we need to find the pattern length
  // Priority: use px values if available, otherwise use % values
  let patternLengthPx = 0;
  let patternLengthPercent = 0;
  let hasPxValues = false;

  colorStops.forEach((stop) => {
    if (stop.length) {
      if (stop.length.type === 'px') {
        hasPxValues = true;
        patternLengthPx = Math.max(patternLengthPx, parseFloat(stop.length.value));
      } else if (stop.length.type === '%') {
        patternLengthPercent = Math.max(patternLengthPercent, parseFloat(stop.length.value));
      }
    }
  });

  // Determine the actual pattern length
  let patternLength;
  if (hasPxValues && patternLengthPx > 0) {
    patternLength = patternLengthPx;
  } else if (patternLengthPercent > 0) {
    patternLength = (patternLengthPercent / 100) * maxRadius;
  } else {
    // No explicit pattern length, use the full radius (no repeating)
    patternLength = maxRadius;
  }

  // Avoid division by zero or infinite loops
  if (patternLength <= 0) {
    patternLength = maxRadius;
  }

  // Calculate number of repetitions
  const repetitions = Math.ceil(maxRadius / patternLength);

  // Add repeated color stops
  for (let i = 0; i < repetitions; i++) {
    const patternOffsetPx = i * patternLength;

    colorStops.forEach((stop, index) => {
      let stopPositionPx = 0;

      if (stop.length) {
        if (stop.length.type === 'px') {
          stopPositionPx = parseFloat(stop.length.value);
        } else if (stop.length.type === '%') {
          // For repeating gradients with px-based pattern length,
          // % values are relative to the pattern length
          if (hasPxValues) {
            stopPositionPx = (parseFloat(stop.length.value) / 100) * patternLength;
          } else {
            // Pure percentage-based repeating gradient
            stopPositionPx = (parseFloat(stop.length.value) / 100) * maxRadius;
          }
        }
      } else {
        // No length specified, distribute evenly within the pattern
        stopPositionPx = (index / (colorStops.length - 1)) * patternLength;
      }

      const position = (patternOffsetPx + stopPositionPx) / maxRadius;
      const colorValue = colorStopToString(stop);

      if (colorValue && position >= 0 && position <= 1) {
        gradient.addColorStop(Math.min(Math.max(0, position), 1), colorValue);
      }
    });
  }

  return gradient;
}
